import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];

export interface TaskWithProject extends Task {
    project: Project;
}

/**
 * Hook to fetch all tasks assigned to a specific team member
 * Tasks are sorted chronologically by start_date
 * 
 * @param memberId - The ID of the team member, or null if no member selected
 * @returns Query result with tasks including project information
 */
export const useMemberTasks = (memberId: number | null) => {
    return useQuery({
        queryKey: ['member-tasks', memberId],
        queryFn: async (): Promise<TaskWithProject[]> => {
            if (!memberId) return [];

            const { data, error } = await supabase
                .from('tasks')
                .select('*, project:projects(*)')
                .eq('assigned_member_id', memberId)
                .order('start_date', { ascending: true });

            if (error) {
                throw new Error(`Failed to fetch tasks for member ${memberId}: ${error.message}`);
            }

            return (data || []) as TaskWithProject[];
        },
        enabled: memberId !== null,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

